import React, { ComponentType, forwardRef, ReactElement, ReactNode, RefObject } from 'react';
import classNames from 'classnames';

import { useMessageFormatter } from '@redsift/design-system';
import intlMessages from './intl';

import { mdiAlert, mdiAlertCircle, mdiCheckCircle, mdiClose, mdiInformation } from '@redsift/icons';
import {
  NotificationsColorPalette,
  Comp,
  getStylingTransientProps,
  Icon,
  Flexbox,
  Spinner,
  Text,
  isComponent,
  ButtonColor,
  IconButton,
  RedsiftTypographyFontWeightSemiBold,
  useTheme,
} from '@redsift/design-system';
import { StyledToast } from './styles';
import { ToastProps, ToastVariant } from './types';
import { ToastContentProps } from 'react-toastify';

const COMPONENT_NAME = 'Toast';
const CLASSNAME = 'redsift-toast';

/**
 * The Design System Button or IconButton in `node`, looking through a styled()
 * wrapper — `styled(Button)` renders as its own component, so a displayName
 * check alone would miss it.
 */
const asActionButton = (node: ReactNode): ReactElement | undefined => {
  if (isComponent('Button')(node) || isComponent('IconButton')(node)) return node;
  if (!React.isValidElement(node)) return undefined;

  const wrapped = (node.type as { target?: ComponentType & { displayName?: string } }).target;

  return wrapped?.displayName === 'Button' || wrapped?.displayName === 'IconButton' ? node : undefined;
};

const getVariant = (variant: ToastVariant) => {
  switch (variant) {
    case ToastVariant.info:
    default:
      return <Icon icon={mdiInformation} color={NotificationsColorPalette.info} />;
    case ToastVariant.error:
      return <Icon icon={mdiAlertCircle} color={NotificationsColorPalette.error} />;
    case ToastVariant.success:
      return <Icon icon={mdiCheckCircle} color={NotificationsColorPalette.success} />;
    case ToastVariant.warning:
      return <Icon icon={mdiAlert} color={NotificationsColorPalette.warning} />;
    case ToastVariant.loading:
      return <Spinner size="small" />;
  }
};

/**
 * Toast displays non-blocking feedback messages. Raise one with the `toast`
 * singleton or the `useToast` hook; render this component directly only when
 * you need the shape on its own (a story, a page-level banner).
 *
 * Variants: `info`, `success`, `warning`, `error`, `loading`
 *
 * @example
 * // Setup toast container (once in app root)
 * import { ToastContainer } from '@redsift/popovers';
 * <App><ToastContainer /></App>
 *
 * @example
 * // Show info toast
 * import { toast } from '@redsift/popovers';
 * toast.info({ content: 'Your changes have been saved' });
 *
 * @example
 * // Show error toast with title
 * toast.error({ title: 'Error', content: 'Failed to save' });
 *
 * @example
 * // Loading toast that resolves
 * const id = toast.loading({ content: 'Uploading...', autoClose: false });
 * await uploadFile();
 * toast.update(id, { content: 'Upload complete', variant: 'success', autoClose: 5000 });
 *
 * @example
 * // Toast with a custom action in place of the close button. A Design System
 * // Button or IconButton takes the variant colour and dismisses the Toast on
 * // click; any other node is rendered as given.
 * toast.info({
 *   content: 'File deleted',
 *   closeButton: <Button onClick={undo}>Undo</Button>,
 * });
 */
export const Toast: Comp<ToastProps & Partial<ToastContentProps>, HTMLDivElement> = forwardRef((props, ref) => {
  const { transientProps, otherProps } = getStylingTransientProps(props);

  const {
    children,
    className,
    closeButton = true,
    closeToast,
    theme: propsTheme,
    title,
    variant = 'info',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    toastProps,
    ...forwardedProps
  } = otherProps;

  const theme = useTheme(propsTheme);

  const format = useMessageFormatter(intlMessages);

  const icon = getVariant(variant!);
  const actionButton = asActionButton(closeButton);

  return (
    <StyledToast
      {...forwardedProps}
      {...transientProps}
      className={classNames(Toast.className, className)}
      ref={ref as RefObject<HTMLDivElement>}
      $hasTitle={Boolean(title)}
      $variant={variant}
      $theme={theme}
    >
      <Flexbox className={`${Toast.className}__header`} justifyContent="space-between" alignItems="center">
        <Flexbox justifyContent="space-between" flexDirection="column" gap="0">
          <Flexbox className={`${Toast.className}-header__title`} gap="14px" alignItems="flex-start">
            {icon}
            {title ? <Text fontWeight={RedsiftTypographyFontWeightSemiBold}>{title}</Text> : null}
          </Flexbox>

          {children ? <div className={`${Toast.className}__content`}>{children}</div> : null}
        </Flexbox>

        {closeButton === true ? (
          <IconButton
            aria-label={format('close')}
            className={`${Toast.className}-header__icon-button`}
            icon={mdiClose}
            onClick={closeToast}
          />
        ) : actionButton ? (
          React.cloneElement(actionButton, {
            color: variant === 'loading' ? 'info' : (variant as ButtonColor),
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
              actionButton.props.onClick?.(event);
              closeToast?.();
            },
          })
        ) : closeButton === false ? null : (
          closeButton
        )}
      </Flexbox>
    </StyledToast>
  );
});
Toast.className = CLASSNAME;
Toast.displayName = COMPONENT_NAME;
